if (!customElements.get('l20-thirteen-imgtabs')) {
  class L20ThirteenImgtabs extends HTMLElement {
    connectedCallback() {
      this.carousel = this.querySelector('[data-imgtabs-carousel]');
      this.track = this.querySelector('.l20-thirteen-imgtabs__track');
      this.slides = Array.from(this.querySelectorAll('[data-imgtabs-slide]'));
      this.previousButton = this.querySelector('[data-imgtabs-prev]');
      this.nextButton = this.querySelector('[data-imgtabs-next]');
      this.liveRegion = this.querySelector('[data-imgtabs-live]');
      this.currentIndex = 0;
      this.timer = null;
      this.isPointerDown = false;
      this.isDragging = false;
      this.dragStartX = 0;
      this.dragStartScrollLeft = 0;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!this.carousel || !this.slides.length) return;
      this.onPrevious = () => this.goTo(this.currentIndex - 1, true);
      this.onNext = () => this.goTo(this.currentIndex + 1, true);
      this.onKeydown = (event) => this.handleKeydown(event);
      this.onScroll = () => this.updateIndex();
      this.onMouseEnter = () => this.stopAutoplay();
      this.onMouseLeave = () => this.startAutoplay();
      this.onFocusIn = () => this.stopAutoplay();
      this.onFocusOut = () => this.startAutoplay();
      this.onPointerDown = (event) => this.handlePointerDown(event);
      this.onPointerMove = (event) => this.handlePointerMove(event);
      this.onPointerUp = (event) => this.handlePointerEnd(event);
      this.onPointerCancel = (event) => this.handlePointerEnd(event);
      this.onVisibilityChange = () => document.hidden ? this.stopAutoplay() : this.startAutoplay();
      this.onBlockSelect = (event) => this.handleBlockSelect(event);
      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);
      this.carousel.addEventListener('keydown', this.onKeydown);
      this.carousel.addEventListener('scroll', this.onScroll, { passive: true });
      this.carousel.addEventListener('pointerdown', this.onPointerDown);
      this.carousel.addEventListener('pointermove', this.onPointerMove);
      this.carousel.addEventListener('pointerup', this.onPointerUp);
      this.carousel.addEventListener('pointercancel', this.onPointerCancel);
      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.startAutoplay();
    }
    disconnectedCallback() {
      this.stopAutoplay();
      this.previousButton?.removeEventListener('click', this.onPrevious);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.carousel?.removeEventListener('keydown', this.onKeydown);
      this.carousel?.removeEventListener('scroll', this.onScroll);
      this.carousel?.removeEventListener('pointerdown', this.onPointerDown);
      this.carousel?.removeEventListener('pointermove', this.onPointerMove);
      this.carousel?.removeEventListener('pointerup', this.onPointerUp);
      this.carousel?.removeEventListener('pointercancel', this.onPointerCancel);
      this.carousel?.classList.remove('is-dragging');
      this.removeEventListener('mouseenter', this.onMouseEnter);
      this.removeEventListener('mouseleave', this.onMouseLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
    goTo(index, announce = false) {
      const normalized = (index + this.slides.length) % this.slides.length;
      this.currentIndex = normalized;
      const trackPadding = this.track ? Number.parseFloat(window.getComputedStyle(this.track).paddingLeft) || 0 : 0;
      const targetLeft = Math.max(0, this.slides[normalized].offsetLeft - trackPadding);
      this.carousel.scrollTo({ left: targetLeft, behavior: this.reducedMotion ? 'auto' : 'smooth' });
      if (announce && this.liveRegion) this.liveRegion.textContent = `Slide ${normalized + 1} of ${this.slides.length}`;
      this.restartAutoplay();
    }
    handlePointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;

      this.isPointerDown = true;
      this.isDragging = false;
      this.dragStartX = event.clientX;
      this.dragStartScrollLeft = this.carousel.scrollLeft;
      this.stopAutoplay();
      this.carousel.setPointerCapture?.(event.pointerId);
    }
    handlePointerMove(event) {
      if (!this.isPointerDown) return;

      const distance = event.clientX - this.dragStartX;
      if (!this.isDragging && Math.abs(distance) < 4) return;

      this.isDragging = true;
      this.carousel.classList.add('is-dragging');
      event.preventDefault();
      this.carousel.scrollLeft = this.dragStartScrollLeft - distance;
    }
    handlePointerEnd(event) {
      if (!this.isPointerDown) return;

      const wasDragging = this.isDragging;
      this.isPointerDown = false;
      this.isDragging = false;
      this.carousel.classList.remove('is-dragging');
      if (event.pointerId != null && this.carousel.hasPointerCapture?.(event.pointerId)) {
        this.carousel.releasePointerCapture(event.pointerId);
      }

      if (wasDragging) {
        event.preventDefault();
        this.goTo(this.getNearestIndex());
        return;
      }

      this.startAutoplay();
    }
    getNearestIndex() {
      const left = this.carousel.scrollLeft;
      let closestIndex = 0;
      let closestDistance = Infinity;
      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - left);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      return closestIndex;
    }
    updateIndex() {
      this.currentIndex = this.getNearestIndex();
    }
    handleKeydown(event) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'ArrowLeft') this.goTo(this.currentIndex - 1, true);
      if (event.key === 'ArrowRight') this.goTo(this.currentIndex + 1, true);
      if (event.key === 'Home') this.goTo(0, true);
      if (event.key === 'End') this.goTo(this.slides.length - 1, true);
    }
    startAutoplay() {
      if (this.dataset.autoplay !== 'true' || this.reducedMotion || this.slides.length < 2 || document.hidden || this.timer) return;
      const speed = Number(this.dataset.autoplaySpeed) || 5000;
      this.timer = window.setInterval(() => this.goTo(this.currentIndex + 1), speed);
    }
    stopAutoplay() { window.clearInterval(this.timer); this.timer = null; }
    restartAutoplay() { this.stopAutoplay(); this.startAutoplay(); }
    handleBlockSelect(event) {
      const slide = event.target.closest('[data-imgtabs-slide]');
      if (!slide) return;
      this.stopAutoplay();
      this.goTo(Number(slide.dataset.slideIndex));
    }
  }
  customElements.define('l20-thirteen-imgtabs', L20ThirteenImgtabs);
}
